import { AuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { ReactNode, useEffect } from 'react';

import { globalConfig } from './configuration/config';

import type { User } from 'oidc-client-ts';

interface Cree8AuthProviderProps {
  children: ReactNode;
}

/** App state we round-trip through the OAuth `state` parameter. */
export interface Cree8SigninState {
  returnTo: string;
}

/**
 * The page the user was trying to reach, in a form that is safe to hand to
 * `history.replaceState` on return. Deliberately path-only: a value starting
 * with `//` or a scheme would let a crafted link bounce the user off-site
 * after a successful login. `/login` maps to `/` so returning there cannot
 * kick off another sign-in.
 */
export const currentSigninState = (): Cree8SigninState => {
  const { pathname, search } = window.location;
  return { returnTo: pathname === '/login' ? '/' : `${pathname}${search}` };
};

/**
 * Reads the return path back off the OAuth `state`. The value round-trips
 * through storage under the user's control, so anything that isn't a plain
 * app-relative path falls back to the app root.
 */
export const safeReturnTo = (state: unknown): string => {
  const returnTo = (state as Cree8SigninState | undefined)?.returnTo;
  if (typeof returnTo !== 'string') return '/';
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) return '/';
  return returnTo;
};

/**
 * CREE8 OIDC provider (public client, Auth Code + PKCE).
 *
 * - authority/client_id/apiAudience come from runtime config.
 * - redirect_uri is the app origin, matched byte-for-byte by CREE8 against the
 *   single registered URI. Return routing must ride in `state`, never appended
 *   to the URI — deep links such as `/accept-invite/:id?token=…` are restored
 *   from `user.state` in `onSigninCallback` instead.
 * - `cree8` releases the system `role` claim (needed for Producer admin gates).
 * - `resource` mints the access token for the CREE8 API audience.
 * - After a successful login we strip the OAuth callback params from the URL.
 */
export const Cree8AuthProvider = ({ children }: Cree8AuthProviderProps) => {
  const { issuer, clientId, apiAudience } = globalConfig.config.cree8;

  return (
    <AuthProvider
      authority={issuer}
      client_id={clientId}
      redirect_uri={window.location.origin}
      post_logout_redirect_uri={window.location.origin}
      response_type="code"
      scope="openid offline_access email profile cree8"
      extraQueryParams={{ resource: apiAudience }}
      automaticSilentRenew
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      onSigninCallback={(user: User | undefined) => {
        window.history.replaceState({}, document.title, safeReturnTo(user?.state));
      }}
    >
      {children}
    </AuthProvider>
  );
};

export const useAuth = () => useOidcAuth();

/** Kick off sign-in when the user lands on `/login`. */
export const useLoginRoute = () => {
  const auth = useOidcAuth();

  useEffect(() => {
    if (window.location.pathname === '/login' && !auth.isAuthenticated && !auth.isLoading) {
      void auth.signinRedirect({ state: currentSigninState() });
    }
  }, [auth.isAuthenticated, auth.isLoading]);
};
